import {
	type RemixiconComponentType,
	RiAlertLine,
	RiInformationLine,
	RiLightbulbLine,
	RiSpam2Line,
	RiSpamLine,
} from '@remixicon/react';
import type { PropsWithChildren, ReactNode } from 'react';

import { cn } from '@playbook/utils';

import css from './styles.module.css';

export const alertTypes = [
	'note',
	'tip',
	'important',
	'warning',
	'caution',
] as const;

export type AlertType = (typeof alertTypes)[number];

const icons: Record<AlertType, RemixiconComponentType> = {
	note: RiInformationLine,
	tip: RiLightbulbLine,
	important: RiSpamLine,
	warning: RiAlertLine,
	caution: RiSpam2Line,
};

const labels: Record<AlertType, string> = {
	note: 'Note',
	tip: 'Tip',
	important: 'Important',
	warning: 'Warning',
	caution: 'Caution',
};

interface AlertProps extends PropsWithChildren {
	type: AlertType;
	title?: string;
}

/**
 *	A GitHub markdown alert — the `> [!NOTE]` blockquote. Rendered from the
 *	blockquote node override in @shared/docs/markdoc, never written by hand.
 *
 *	`title` is what the author wrote after the marker, when they wrote anything;
 *	the type's own label stands in otherwise.
 */
export function Alert({ type, title, children }: AlertProps): ReactNode {
	const Icon = icons[type];

	return (
		<aside data-alert={type} className={cn(css.alert, css[type])}>
			<p className={css.title}>
				<Icon aria-hidden="true" className={css.icon} />
				{title ?? labels[type]}
			</p>
			{children}
		</aside>
	);
}
